import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClipboard } from '../context/ClipboardContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
    Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, List, ListItem, ListItemText, TextField,
    Alert, IconButton, Autocomplete, createFilterOptions,
    CircularProgress, Chip, FormControlLabel, Switch,
    Backdrop, Snackbar, Badge, Tooltip
} from '@mui/material';

import {
    Search as SearchIcon, Add as AddIcon, Print as PrintIcon, Close as CloseIcon,
    Save as SaveIcon, CloudUpload as CloudUploadIcon, ExitToApp as ExitIcon,
    NoteAdd as NoteAddIcon, Delete as DeleteIcon,
    ShoppingCart as ShoppingCartIcon,
	Edit as EditIcon
} from '@mui/icons-material';

import { Editor } from '@tinymce/tinymce-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

interface Patient { id: string; name: string; lastName: string; birthDate?: string; patientId?: string; cedula?: string; idNumber?: string; }
interface Medication { 
    id: string; commercialName: string; activeIngredient: string; presentation: string; concentration?: string;
    form: string; dosage?: string; isBrand?: boolean; originalPosology?: string;
}
interface Recipe { id: string; patientId: string; medications: Medication[]; createdAt: string; }
interface Config { logoRecipeUrl?: string; footerAddress?: string; headerImageUrl?: string; logoUrl?: string; }

function RecipesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { clipboardItems, clearClipboard, removeItem } = useClipboard();
    
    // --- ESTADOS ---
    const [allPatients, setAllPatients] = useState<Patient[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [config, setConfig] = useState<Config>({});
    const [recipeContent, setRecipeContent] = useState('');
    const [currentSignature, setCurrentSignature] = useState('');

    // Buscador y Datos
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<Medication[]>([]);
    const [isSearchingMeds, setIsSearchingMeds] = useState(false);
    const [selectedMeds, setSelectedMeds] = useState<Medication[]>([]); 
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentMedToEdit, setCurrentMedToEdit] = useState<Medication | null>(null);
    const [pastRecipes, setPastRecipes] = useState<Recipe[]>([]);

    // Configuración UI
    const [includeSignature, setIncludeSignature] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processStatus, setProcessStatus] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [openCartDialog, setOpenCartDialog] = useState(false);
    
    // PAGINACIÓN
    const [pages, setPages] = useState<string[]>(['']); 
    const [activePage, setActivePage] = useState(0);
    const [pageMedications, setPageMedications] = useState<Record<number, Medication[]>>({ 0: [] });

    // ✅ Contar medicamentos en el carrito (excluyendo estudios)
    const medicationsInCart = Array.isArray(clipboardItems) 
        ? clipboardItems.filter((item: any) => item.type !== 'study_request')
        : [];

    // --- UTILS ---
    const calculateAge = (birthDate?: string) => {
        if (!birthDate) return '---';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
        return `${age} años`;
    };

    const processImageUrl = useCallback((url: string | undefined) => {
        if (!url) return '';
        if (url.startsWith('data:') || url.startsWith('http')) return url;
        const cleanPath = url.startsWith('/') ? url.slice(1) : url;
        return `${API_URL}/${cleanPath}?t=${new Date().getTime()}`;
    }, []);

    const imageUrlToBase64 = async (url: string): Promise<string> => {
        try {
            const token = localStorage.getItem('token');
            const fullUrl = processImageUrl(url);
            const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(fullUrl)}`;
            const response = await fetch(proxyUrl, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error();
            const data = await response.json();
            return data.base64;
        } catch {
            return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
        }
    };

    // --- GENERADOR HTML ---
    const generateTemplateHtml = useCallback(async (meds: Medication[], patient: Patient | null, isForNewPage: boolean = false) => {
        if (!patient) return '';

        const rawLogoUrl = config.logoRecipeUrl || config.logoUrl || config.headerImageUrl;
        const rawSignatureUrl = currentSignature || (user as any)?.signature_url;
        
        let logoUrl = '', signatureUrl = '';
        try {
            if(rawLogoUrl) logoUrl = await imageUrlToBase64(rawLogoUrl);
            if(rawSignatureUrl) signatureUrl = await imageUrlToBase64(rawSignatureUrl);
        } catch(e) {}

        const logoHtml = logoUrl 
            ? `<img src="${logoUrl}" style="max-height: 50px; width: auto; display: block; margin: 0 auto 5px auto;">` 
            : '<div style="height: 5px;"></div>';

        const sigHtml = includeSignature && signatureUrl 
            ? `<div style="text-align: center; margin-top: 5px;">
                 <img src="${signatureUrl}" style="max-height: 80px; width: auto; display: block; margin: 0 auto;">
               </div>`
            : `<div style="margin-top: 10px; text-align: center;">
                 <div style="height: 50px; border-bottom: 1px solid #000; width: 140px; margin: 0 auto;"></div>
                 <small style="font-size: 7pt;">SELLO Y FIRMA</small>
               </div>`;

        const footerHtml = config?.footerAddress 
            ? `<div style="margin-top: 5px; border-top: 0.3px solid #ccc; padding-top: 3px; text-align: center; font-size: 7pt; color: #666; font-family: Arial;">
                 ${config.footerAddress}
               </div>` : '';

        const displayCedula = patient.cedula || patient.patientId || '---';

        const patientBox = `
            <div style="border: 1px solid #000; padding: 4px; margin-bottom: 5px; font-size: 8pt; font-family: Arial;">
                <table style="width: 100%; border-collapse: collapse;">
                    <tr>
                        <td style="text-align: left;"><b>NOMBRE:</b> ${patient.name.toUpperCase()} ${patient.lastName.toUpperCase()}</td>
                        <td style="text-align: right; width: 100px;"><b>EDAD:</b> ${calculateAge(patient.birthDate)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left;"><b>C.I.:</b> ${displayCedula}</td>
                        <td style="text-align: right;"><b>FECHA:</b> ${new Date().toLocaleDateString()}</td>
                    </tr>
                </table>
            </div>`;

        const CONTENT_HEIGHT = "470px"; 

        // 1. AQUÍ CONTROLAS LA IZQUIERDA (Nombres y Componentes)
        const medsContentLeft = meds.length > 0 
            ? meds.map((m, i) => `
                <p style="margin: 0 0 5px 0;">
                    <b style="font-size: 10pt;">${i+1}. ${m.commercialName}</b><br/>
                    <span style="font-size: 8pt;">(${m.activeIngredient} - ${m.concentration || ''} ${m.form || m.presentation || ''})</span>
                </p>`).join('')
            : (isForNewPage ? `<p style="color:#ccc; font-style:italic; margin-top:20px;">Haga clic para escribir...</p>` : '');

        // 2. AQUÍ CONTROLAS LA DERECHA (Posología / Indicaciones)
        const medsContentRight = meds.length > 0
            ? meds.map((m, i) => `
                <p style="margin: 0 0 8px 0;">
                    <b style="font-size: 10pt;">${i+1}. ${m.commercialName}</b><br/>
                    
                    <span style="font-size: 9pt;">
                        ${m.dosage || 'Según indicación médica.'}
                    </span>
                    
                </p>`).join('')
            : (isForNewPage ? `<p style="color:#ccc; font-style:italic; margin-top:20px;">Indicaciones...</p>` : '');

        return `
        <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-family: Arial;">
          <tr>
            <td style="width: calc(50% - 7mm); vertical-align: top; padding-right: 5px;">
                ${logoHtml} ${patientBox}
                <div style="text-align: right; font-style: italic; font-size: 10pt; margin: 2px 0;">Rp.</div>
                
                <div style="min-height: ${CONTENT_HEIGHT}; font-size: 10pt; line-height: 1.1;">
                    ${medsContentLeft}
                </div>
                
                ${sigHtml} ${footerHtml}
            </td>
            <td style="width: 14mm;">&nbsp;</td>
            <td style="width: calc(50% - 7mm); vertical-align: top; padding-left: 5px;">
                ${logoHtml} ${patientBox}
                <div style="text-align: right; font-style: italic; font-size: 10pt; margin: 2px 0;">Ind.</div>
                
                <div style="min-height: ${CONTENT_HEIGHT}; font-size: 10pt; line-height: 1.1;">
                    ${medsContentRight}
                </div>
                
                ${sigHtml} ${footerHtml}
            </td>
          </tr>
        </table>`;
		
		
    }, [config, includeSignature, currentSignature, user, processImageUrl]);

    const updateCurrentPageEditor = useCallback(async (meds: Medication[], pIndex: number) => {
        if (!selectedPatient) return;
        const html = await generateTemplateHtml(meds, selectedPatient, false);
        setPages(prev => {
            const newPages = [...prev];
            newPages[pIndex] = html;
            return newPages;
        });
        if (pIndex === 0) setRecipeContent(html);
    }, [selectedPatient, generateTemplateHtml]);

    // ✅ PEGAR DESDE CARRITO (MANUAL)
    const handlePasteFromCart = async () => {
        if (medicationsInCart.length === 0) {
            setError("No hay medicamentos en el carrito.");
            return;
        }

        const newMeds: Medication[] = medicationsInCart.map((item: any) => ({
            id: Math.random().toString(36).substr(2, 9),
            commercialName: item.commercialName || item.name || 'Medicamento',
            activeIngredient: item.activeIngredient || 'Genérico',
            presentation: item.presentation || '',
            form: item.form || '',
            dosage: item.dosage || item.originalPosology || '',
            isBrand: item.isBrand || false,
            originalPosology: item.originalPosology
        }));

        // Agregar a la página actual
        const currentPageMeds = [...(pageMedications[activePage] || []), ...newMeds];
        setPageMedications(prev => ({ ...prev, [activePage]: currentPageMeds }));
        
        // Agregar a la lista global
        setSelectedMeds(prev => [...prev, ...newMeds]);

        // Actualizar editor visual
        await updateCurrentPageEditor(currentPageMeds, activePage);

        setSuccess(`✅ ${newMeds.length} medicamentos agregados a la receta.`);
        setOpenCartDialog(false);
        clearClipboard();
    };

    // --- CARGA INICIAL DE DATOS Y FIRMA ---
    useEffect(() => {
        const init = async () => {
            const token = localStorage.getItem('token');
            if (!token) { navigate('/login'); return; }
            try {
                const headers = {'Authorization': `Bearer ${token}`};
                const [pRes, cRes] = await Promise.all([
                    fetch(`${API_URL}/api/patients`, { headers }),
                    fetch(`${API_URL}/api/config`, { headers })
                ]);
                if (pRes.ok) setAllPatients(await pRes.json());
                if (cRes.ok) setConfig(await cRes.json());

                if (user?.id) {
                    const uRes = await fetch(`${API_URL}/api/users/${user.id}`, { headers });
                    if (uRes.ok) {
                        const uData = await uRes.json();
                        setCurrentSignature(uData.signatureUrl || uData.signature_url);
                    }
                }
            } catch (e) {} finally { setIsInitialLoading(false); }
        };
        init();
    }, [navigate, user]);

    // Cargar historial al seleccionar paciente
    useEffect(() => {
        if (selectedPatient) {
            const fetchHist = async () => {
                try {
                    const res = await fetch(`${API_URL}/api/recipes?patientId=${selectedPatient.id}`, { 
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
                    });
                    if (res.ok) {
                        const data = await res.json();
                        setPastRecipes(data.filter((r:any) => r.type === 'medication'));
                    }
                } catch(e) {}
            };
            fetchHist();
            updateCurrentPageEditor(pageMedications[activePage] || [], activePage);
        }
    }, [selectedPatient]);

    // Reactivar firma cuando cambia el switch
    useEffect(() => {
        if (selectedPatient) {
            updateCurrentPageEditor(pageMedications[activePage] || [], activePage);
        }
    }, [includeSignature, activePage, selectedPatient]);

    // --- MANEJO DE PÁGINAS ---
    const handleAddPage = async () => {
        if (!selectedPatient) return;
        const emptyTemplate = await generateTemplateHtml([], selectedPatient, true);
        const newIndex = pages.length;
        setPages(prev => [...prev, emptyTemplate]);
        setPageMedications(prev => ({ ...prev, [newIndex]: [] }));
        setTimeout(() => setActivePage(newIndex), 100);
    };

    const handleDeletePage = (index: number) => {
        if (pages.length > 1) {
            setPages(prev => prev.filter((_, i) => i !== index));
        }
    };

    // --- MANEJO DE MEDICAMENTOS ---
    const handleAddMedication = (med: Medication) => {
        setCurrentMedToEdit({ ...med, id: Math.random().toString(36).substr(2, 9) });
        setEditDialogOpen(true);
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleSaveEditedMed = () => {
        if (currentMedToEdit) {
            const currentPageMeds = [...(pageMedications[activePage] || []), currentMedToEdit];
            setPageMedications(prev => ({ ...prev, [activePage]: currentPageMeds }));
            setSelectedMeds(prev => [...prev, currentMedToEdit]);
            updateCurrentPageEditor(currentPageMeds, activePage);
            setEditDialogOpen(false);
            setCurrentMedToEdit(null);
        }
    };

// --- NUEVAS FUNCIONES PARA GESTIÓN DE MEDICAMENTOS ---

    // 1. Eliminar un medicamento YA agregado a la página actual
    const handleRemoveFromPage = async (indexToRemove: number) => {
        const currentMeds = pageMedications[activePage] || [];
        // Filtramos para quitar el que coincida con el índice
        const newMeds = currentMeds.filter((_, i) => i !== indexToRemove);
        
        // Actualizamos estado
        setPageMedications(prev => ({ ...prev, [activePage]: newMeds }));
        
        // Importante: Regeneramos el HTML visual para que desaparezca del editor
        await updateCurrentPageEditor(newMeds, activePage);
    };

    // 2. Procesar un item INDIVIDUAL del carrito (Editar -> Agregar)
    const handleEditFromCart = (cartItem: any) => {
        // Convertimos el item del carrito al formato de medicamento
        const medToEdit: Medication = {
            id: Math.random().toString(36).substr(2, 9),
            commercialName: cartItem.name || cartItem.commercialName,
            activeIngredient: cartItem.activeIngredient || 'Genérico',
            presentation: cartItem.presentation || '',
            form: cartItem.form || '',
            dosage: cartItem.originalPosology || '', // Traemos la posología larga para que la edites
            isBrand: cartItem.isBrand || false,
            originalPosology: cartItem.originalPosology
        };

        // Abrimos el modal de edición existente
        setCurrentMedToEdit(medToEdit);
        setEditDialogOpen(true);
        // Opcional: Cerrar el carrito para enfocar en la edición
        // setOpenCartDialog(false); 
    };

    // --- GUARDADO ---
    const generatePDFBlob = async () => {
        const pdf = new jsPDF('l', 'mm', 'letter'); 
        const pageWidth = 279.4;
        const pageHeight = 215.9;

        for (let i = 0; i < pages.length; i++) {
            const element = document.getElementById(`recipe-canvas-${i}`);
            if (!element) continue;

            const canvas = await html2canvas(element, { 
                scale: 2, 
                useCORS: true, 
                backgroundColor: "#ffffff",
                onclone: (clonedDoc) => {
                    const toolbars = clonedDoc.querySelectorAll('[id^="toolbar-recipe-"]');
                    toolbars.forEach((el: any) => el.style.display = 'none');
                    const paper = clonedDoc.querySelector(`.recipe-canvas`);
                    if (paper) {
                        (paper as HTMLElement).style.boxShadow = 'none';
                        (paper as HTMLElement).style.border = 'none';
                    }
                }
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            if (i > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
        }
        return pdf;
    };

    const handlePrintViaPDF = async () => {
        if (!selectedPatient) return;
        setIsProcessing(true);
        setProcessStatus("Generando documento...");
        
        try {
            const pdf = await generatePDFBlob();
            const blob = pdf.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            
            iframe.onload = () => {
                setTimeout(() => {
                    const win = iframe.contentWindow;
                    if (!win) return;

                    win.focus();

                    // ✅ TRUCO PRO: Apagar el spinner SOLO cuando cierres la ventana de impresión
                    // Esto mantiene la pantalla gris durante el "lag" de la PC lenta.
                    win.onafterprint = () => {
                        setProcessStatus("");
                        setIsProcessing(false);
                        // Limpieza opcional
                        // document.body.removeChild(iframe);
                        // URL.revokeObjectURL(blobUrl);
                    };

                    // Ejecutar impresión
                    win.print();

                    // 🛡️ SEGURO DE VIDA: Si el navegador no detecta el cierre, 
                    // quitar el spinner forzosamente a los 8 segundos para no bloquear la app.
                    // (Solo actuará si onafterprint falla).
                    setTimeout(() => {
                        setIsProcessing(false);
                    }, 8000); 

                }, 1000); // 1 segundo de cortesía inicial
            };
        } catch (e) {
            console.error(e);
            setError("Error al generar la impresión.");
            setIsProcessing(false);
        }
    };

    const handleSavePDFCloud = async () => {
        if (!selectedPatient) return;
        setIsProcessing(true);
        setProcessStatus("Subiendo PDF...");
        try {
            const pdf = await generatePDFBlob();
            const formData = new FormData();
            formData.append('file', pdf.output('blob'), `Receta_${selectedPatient.lastName}.pdf`);
            formData.append('patientId', selectedPatient.id);
            formData.append('title', `Receta - ${new Date().toLocaleDateString()}`);
            formData.append('category', 'RECIPE');

            await fetch(`${API_URL}/api/documents`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            setSuccess("✅ PDF Guardado");
        } catch (e) { setError("Error subiendo PDF."); } finally { setIsProcessing(false); }
    };

    const handleSaveData = async () => {
        if (!selectedPatient) { setError("Seleccione paciente."); return; }
        setIsProcessing(true);
        try {
            const recipeData = { 
                patientId: selectedPatient.id, medications: selectedMeds,
                doctorName: user?.name, doctorId: user?.id, type: 'medication',
                content: pages.join('\n\n')
            };
            await fetch(`${API_URL}/api/recipes`, { 
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }, 
                body: JSON.stringify(recipeData) 
            });
            setSuccess("✅ Datos Guardados");
        } catch (e) { setError("Error al guardar datos."); }
        setIsProcessing(false);
    };

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchTerm.length < 2) { setSearchResults([]); return; }
            setIsSearchingMeds(true);
            try {
                const res = await fetch(`${API_URL}/api/vademecum?q=${encodeURIComponent(searchTerm)}`, { headers: {'Authorization': `Bearer ${localStorage.getItem('token')}`} });
                if (res.ok) {
                    const data = await res.json();
                    setSearchResults(data.map((m:any) => ({
                        id: m.id, commercialName: m.name, activeIngredient: m.activeIngredient,
                        presentation: m.presentation || '', form: m.pharmaceuticalForm || '', concentration: m.concentration || '', 
                        isBrand: false, originalPosology: m.posology
                    })));
                }
            } catch(e) {} finally { setIsSearchingMeds(false); }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    if (isInitialLoading) return <Box sx={{display:'flex',justifyContent:'center',mt:10}}><CircularProgress /></Box>;

    if (!selectedPatient) return (
        <Dialog open={true} maxWidth="sm" fullWidth>
            <DialogTitle>Seleccionar Paciente</DialogTitle>
            <DialogContent dividers>
                <Autocomplete 
                    options={allPatients} 
                    getOptionLabel={(p) => `${p.name} ${p.lastName} (C.I. N° ${p.cedula || p.patientId || p.idNumber || 'S/C'})`} 
                    onChange={(_,v) => v && setSelectedPatient(v)} 
                    renderInput={(p) => <TextField {...p} label="Buscar Paciente" autoFocus placeholder="Nombre o Cédula" />} 
                />
            </DialogContent>
            <DialogActions><Button onClick={() => navigate('/dashboard')}>Salir</Button></DialogActions>
        </Dialog>
    );

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }} open={isProcessing}>
                <CircularProgress color="inherit" />
                <Typography>{processStatus}</Typography>
            </Backdrop>

            <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'white', borderBottom: '1px solid #ccc', p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="contained" color="error" startIcon={<ExitIcon />} onClick={() => navigate('/dashboard')}>SALIR</Button>
                
                <Box sx={{ position: 'relative' }}>
                    <TextField size="small" placeholder="Medicamento..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ width: 250 }}
                        InputProps={{ startAdornment: <SearchIcon />, endAdornment: isSearchingMeds ? <CircularProgress size={20}/> : searchTerm && <IconButton size="small" onClick={()=>{setSearchTerm('');setSearchResults([])}}><CloseIcon fontSize="small"/></IconButton> }}
                    />
                    {searchResults.length > 0 && (
                        <Paper sx={{ position: 'absolute', top: 45, left: 0, zIndex: 1200, width: 400, maxHeight: 400, overflowY: 'auto', boxShadow: 5 }}>
                            <List dense>{searchResults.map((m, i) => (<ListItem key={i} button onClick={() => handleAddMedication(m)} divider><ListItemText primary={m.commercialName} secondary={`${m.activeIngredient} ${m.presentation}`} /><AddIcon/></ListItem>))}</List>
                        </Paper>
                    )}
                </Box>
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Autocomplete size="small" sx={{ width: 200 }} options={pastRecipes} 
                    getOptionLabel={(o) => `${new Date(o.createdAt).toLocaleDateString()} (${o.medications.length})`}
                    onChange={(_, v) => { if(v) { setPageMedications(p=>({...p,[activePage]:v.medications})); updateCurrentPageEditor(v.medications, activePage); }}}
                    renderInput={(p) => <TextField {...p} label="Historial" placeholder="Seleccione historial..." />}
                />

                <FormControlLabel control={<Switch checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} size="small" />} label={<Typography variant="caption" fontWeight="bold">FIRMA</Typography>} />
                
                {/* ✅ BOTÓN CARRITO - SIEMPRE VISIBLE */}
                <Tooltip title={`Carrito: ${medicationsInCart.length} medicamentos`}>
                    <IconButton 
                        color={medicationsInCart.length > 0 ? "primary" : "default"} 
                        onClick={() => setOpenCartDialog(true)}
                        sx={{ 
                            border: medicationsInCart.length > 0 ? '2px solid #1976d2' : '1px solid #ccc',
                            bgcolor: medicationsInCart.length > 0 ? '#e3f2fd' : 'transparent'
                        }}
                    >
                        <Badge badgeContent={medicationsInCart.length} color="error">
                            <ShoppingCartIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>

                <Button variant="outlined" startIcon={<NoteAddIcon />} onClick={handleAddPage} size="small">+ PÁGINA</Button>
                <Button variant="contained" color="info" onClick={handleSavePDFCloud} startIcon={<CloudUploadIcon />}>NUBE PDF</Button>
                <Button variant="contained" onClick={handlePrintViaPDF} startIcon={<PrintIcon />}>IMPRIMIR</Button>
                <Button variant="contained" color="success" onClick={handleSaveData} startIcon={<SaveIcon />}>GUARDAR</Button>
            </Box>

            <Box sx={{ bgcolor: '#525659', minHeight: '100vh', py: 4, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                {pages.map((html, idx) => (
                    <Box key={idx} onClick={() => setActivePage(idx)} sx={{ position: 'relative' }}>
                        {/* --- MODIFICACIÓN INICIO: BARRA DE CONTROL SUPERIOR --- */}
                        <Box sx={{ position: 'absolute', top: -45, left: 0, right: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            
                            {/* Fila 1: Indicador de página y borrar página */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip label={`Página ${idx + 1}`} color={activePage === idx ? "success" : "default"} size="small" onDelete={pages.length > 1 ? () => handleDeletePage(idx) : undefined} />
                            </Box>

                            {/* Fila 2: Medicamentos en esta página (Aquí está el botón de eliminar medicamento) */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '279mm' }}>
                                {(pageMedications[idx] || []).map((med, mIdx) => (
                                    <Chip 
                                        key={med.id} 
                                        label={`${mIdx + 1}. ${med.commercialName}`} 
                                        onDelete={() => handleRemoveFromPage(mIdx)} // <<-- ESTO BORRA Y REGENERA
                                        color="primary" 
                                        variant="outlined" 
                                        size="small"
                                        sx={{ bgcolor: 'white' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                        {/* --- MODIFICACIÓN FIN --- */}

                        <Paper 
                            id={`recipe-canvas-${idx}`} 
                            className="recipe-canvas"
                            elevation={activePage === idx ? 10 : 2}
                            sx={{ width: '279.4mm', height: '215.9mm', padding: '10mm', bgcolor: 'white', overflow: 'hidden', border: activePage === idx ? '3px solid #2e7d32' : 'none' }}
                        >
                            <Box id={`toolbar-recipe-${idx}`} sx={{ minHeight: 30, bgcolor: '#f5f5f5', mb: 1, borderRadius:1 }} />
                            <Editor
                                tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js"
                                value={html}
                                onEditorChange={(c) => { 
                                    const n = [...pages]; n[idx] = c; setPages(n); 
                                    if(idx===0) setRecipeContent(c); 
                                }}
                                onFocus={() => setActivePage(idx)}
                                init={{
                                    height: '100%', menubar: false, inline: true,
                                    fixed_toolbar_container: `#toolbar-recipe-${idx}`,
                                    plugins: ['table', 'lists', 'image'],
                                    toolbar: 'undo redo | bold italic | bullist numlist | alignleft aligncenter alignright',
                                    content_style: 'body { font-family: Arial; font-size: 10pt; line-height: 1.2; }'
                                }}
                            />
                        </Paper>
                    </Box>
                ))}
            </Box>

            {/* ✅ DIÁLOGO DEL CARRITO */}
            <Dialog open={openCartDialog} onClose={() => setOpenCartDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCartIcon />
                        Carrito de Medicamentos ({medicationsInCart.length})
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {medicationsInCart.length > 0 ? (
                        <List dense>
                            {medicationsInCart.map((item: any, idx: number) => (
                                <ListItem key={idx} divider
                                    secondaryAction={
                                        <Box>
                                            {/* Botón 1: Editar y Agregar (Lápiz) */}
                                            <Tooltip title="Editar posología y agregar">
                                                <IconButton edge="end" color="primary" onClick={() => handleEditFromCart(item)} sx={{ mr: 1 }}>
                                                    <EditIcon /> {/* Asegúrate de importar Edit as EditIcon de mui icons */}
                                                </IconButton>
                                            </Tooltip>
                                            
                                            {/* Botón 2: Eliminar del carrito (Basura) */}
                                            <Tooltip title="Quitar del carrito">
                                                <IconButton edge="end" color="error" onClick={() => removeItem(item.cartId)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                >
                                    <ListItemText 
                                        primary={<Typography fontWeight="bold">{item.name || item.commercialName}</Typography>}
                                        // Mostramos solo un extracto de la posología para no saturar
                                        secondary={
                                            <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {item.dosage || item.originalPosology || 'Sin indicaciones'}
                                            </Typography>
                                        } 
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <ShoppingCartIcon sx={{ fontSize: 60, color: '#ccc' }} />
                            <Typography color="textSecondary" sx={{ mt: 2 }}>
                                El carrito está vacío.
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Agregue medicamentos desde el Vademécum.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setOpenCartDialog(false)}>Cerrar</Button>
                    <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={() => { clearClipboard(); setSuccess("Carrito vaciado"); }}
                        disabled={medicationsInCart.length === 0}
                    >
                        Vaciar Carrito
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success"
                        onClick={handlePasteFromCart}
                        disabled={medicationsInCart.length === 0}
                        startIcon={<AddIcon />}
                    >
                        Agregar a Receta
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth>
                <DialogTitle>{currentMedToEdit?.commercialName}</DialogTitle>
                <DialogContent dividers>
                    {currentMedToEdit?.originalPosology && <Alert severity="info" sx={{mb:2}}>{currentMedToEdit.originalPosology}</Alert>}
                    <TextField fullWidth multiline rows={3} label="Instrucciones" value={currentMedToEdit?.dosage || ''} onChange={(e) => setCurrentMedToEdit(p => p ? {...p, dosage: e.target.value} : null)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveEditedMed}>Añadir</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}><Alert severity="success">{success}</Alert></Snackbar>
            <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}><Alert severity="error">{error}</Alert></Snackbar>
        </>
    );
}

export default RecipesPage;


//RECIPES CON COMA